import { IGame, IPackageShort, IGameShort, IPaginatedResponse, IPackageGamesArgs, IPaginationMeta, IResponse } from "../interfaces";
import gameLoader from "../utils/GameBatchLoader";
import api from "./index";

// Хелпер для извлечения пагинации из заголовков Yii2
const extractYiiPagination = (headers: Record<string, any> = {}): IPaginationMeta => ({
    currentPage: Number(headers["x-pagination-current-page"]) || 1,
    pageCount: Number(headers["x-pagination-page-count"]) || 1,
    perPage: Number(headers["x-pagination-per-page"]) || 50,
    totalCount: Number(headers["x-pagination-total-count"]) || 0,
});

export const gamesApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getGameById: builder.query<IGame, number>({
            queryFn: async (id, _queryApi, _extraOptions, baseQuery) => {
                try {
                    const game = await gameLoader.load(id, baseQuery);
                    return { data: game };
                } catch (error) {
                    return { error };
                }
            },
            providesTags: (_result, _error, id) => [{ type: "games", id }],
        }),
        getPackagesList: builder.query<IPaginatedResponse<IPackageShort[]>, { page: number }>({
            query: ({ page }) => ({
                url: "game-package",
                method: "GET",
                params: { page, "per-page": 50 },
            }),
            // Сохраняем всё под одним ключом кэша, игнорируя аргумент page
            serializeQueryArgs: ({ endpointName }) => {
                return endpointName;
            },
            transformResponse: (response: IResponse<IPackageShort[]>, meta: any) => {
                return {
                    items: response.data,
                    meta: extractYiiPagination(meta?.headers as Headers),
                };
            },
            // Склеиваем старые данные с новыми
            merge: (currentCache, newItems) => {
                // Добавляем только новые пакеты (исключаем дубликаты на всякий случай)
                const newPackages = newItems.items.filter(
                    newItem => !currentCache.items.some(c => c.id === newItem.id)
                );
                currentCache.items.push(...newPackages);
                currentCache.meta = newItems.meta; // Обновляем данные о пагинации
            },
            // Запрашиваем данные заново только если реально изменилась страница
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.page !== previousArg?.page;
            },
        }),
        getPackageGames: builder.query<IPaginatedResponse<IGameShort[]>, IPackageGamesArgs>({
            query: ({ packageId, page = 1, perPage = 50 }) => ({
                url: `game-package/${packageId}/games`,
                method: "GET",
                params: {
                    "f[package_id]": packageId,
                    page,
                    "per-page": perPage,
                },
            }),
            transformResponse: (response: IResponse<IGameShort[]>, meta: any) => {
                return {
                    items: response.data,
                    meta: extractYiiPagination(meta?.headers as Headers),
                };
            },
            // Разделяем кэш по ID пакета!
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return `${endpointName}-${queryArgs.packageId}`;
            },
            // Склеиваем игры
            merge: (currentCache, newItems) => {
                if (!currentCache.items) currentCache.items = [];

                const newGames = newItems.items.filter(
                    (newItem) => !currentCache.items.some((c) => c.id === newItem.id)
                );
                currentCache.items.push(...newGames);
                currentCache.meta = newItems.meta;
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.page !== previousArg?.page || currentArg?.packageId !== previousArg?.packageId;
            },
            providesTags: (_result, _error, arg) => [{ type: "packageGames", id: arg.packageId }],
        }),
    }),
});

export const {
    useGetGameByIdQuery,
    useGetPackagesListQuery,
    useGetPackageGamesQuery,
    useLazyGetPackageGamesQuery
} = gamesApi;